import {
	computed,
	provide,
	reactive,
	ref,
	type ComputedRef,
	type InjectionKey,
	type Ref,
} from "vue";
import type { ZodType } from "zod";
import type { FieldApi } from "./field";
import type { FieldMeta } from "./field-meta";
import type { MaybeRef, Path, PathValue } from "./types";
import { injectWithSelf } from "./utils";
import { useValidator, type ValidatorApi } from "./validator";

export type GenericValues = Record<string, unknown>;

type ValuesStore<TValues extends GenericValues> = TValues;

export type ErrorsByCause = {
	onBlur?: string | null;
	onChange?: string | null;
};

export type ErrorsBag<TValues extends GenericValues> = {
	[P in Path<TValues>]?: ErrorsByCause;
};

export type FieldMetasBag<TValues extends GenericValues> = {
	[K in Path<TValues>]?: FieldMeta<PathValue<TValues, K>>;
};

export type FormState<TValues extends GenericValues = GenericValues> = {
	values: ValuesStore<TValues>;
	errors: ErrorsBag<TValues>;
	metas: FieldMetasBag<TValues>;
	fields: Map<symbol, FieldApi<any>>;
};

export type TypedSchema<TValues extends GenericValues> = ZodType<TValues>;
export type FormOption = {
	mode?: "default" | "immediate" | "onSubmit";
};

export type InternalFormApi<TValues extends GenericValues> = {
	options: MaybeRef<FormOption>;

	values: ComputedRef<TValues>;

	state: FormState<TValues>;
	validator: ValidatorApi<TValues>;
	submittedOnce: Ref<boolean>;

	/**
	 * Register a field in the form
	 *
	 * @returns The unregister function
	 */
	register(field: FieldApi<any>): () => void;
};

export type InternalFormApiId<TValues extends GenericValues> = InjectionKey<
	InternalFormApi<TValues>
>;

export function createState<TValues extends GenericValues>(): FormState<TValues> {
	const values: TValues = reactive({} as any);
	const errors: ErrorsBag<TValues> = reactive({});
	const metas: FieldMetasBag<TValues> = reactive({});
	const fields = new Map();

	return {
		values,
		errors,
		metas,
		fields,
	};
}
export function createInternalFormApi<TValues extends GenericValues>(
	options: MaybeRef<FormOption>
): [InternalFormApiId<TValues>, InternalFormApi<TValues>] {
	const id: InternalFormApiId<TValues> = Symbol();

	const state = createState<TValues>();

	const api: InternalFormApi<TValues> = {
		options,
		values: computed(() => {
			return state.values;
		}),
		state,
		validator: useValidator(state),
		submittedOnce: ref(false),
		register: (field) => {
			const _id = Symbol();
			state.fields.set(_id, field);

			return () => state.fields.delete(_id);
		},
	};
	provide(id, api);

	return [id, api];
}

export function useInternalFormApi<TValues extends GenericValues>(id: InternalFormApiId<TValues>) {
	const context = injectWithSelf(id);
	if (!context) throw new Error("[Form] Internal error");

	return context;
}
